import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { useDesktopWorkspace } from "../desktop/useDesktopWorkspace"
import { DESKTOP_LAYERS } from "../desktop/desktopLayers"

// ── InWindow context — signals components they're inside a floating window ─────

const InWindowCtx = createContext(false)

export function useInWindow() {
  return useContext(InWindowCtx)
}

export function InWindowProvider({ children }) {
  return <InWindowCtx.Provider value={true}>{children}</InWindowCtx.Provider>
}

// ── Spotlight / FloatingWindow context ─────────────────────────────────────────

const SpotlightCtx = createContext(null)

const RECENT_KEY = "spotlight_recent"
const SESSION_KEY = "spotlight_window_session_v1"
const MAX_RECENT = 6
const MAX_WINDOWS = 12

const scopedKey = (key, accountId, monitorId = "primary") => `${key}:${accountId || "signed-out"}:${monitorId}`

function loadRecent(accountId) {
  try { return JSON.parse(localStorage.getItem(scopedKey(RECENT_KEY, accountId)) ?? "[]") } catch { return [] }
}

function saveRecent(r, accountId) {
  localStorage.setItem(scopedKey(RECENT_KEY, accountId), JSON.stringify(r.slice(0, MAX_RECENT)))
}

function loadWindowSession(accountId, monitorId, enabled) {
  if (!enabled) return []
  try {
    const parsed = JSON.parse(localStorage.getItem(scopedKey(SESSION_KEY, accountId, monitorId)) ?? "[]")
    if (!Array.isArray(parsed)) return []
    const vw = Math.max(640, window.innerWidth)
    const vh = Math.max(420, window.innerHeight)
    return parsed.slice(0, MAX_WINDOWS).filter((item) => item && typeof item.typeId === "string" && item.typeId !== "terminal").map((item, index) => {
      const w = Math.max(300, Math.min(vw, Number(item.w) || 860))
      const h = Math.max(180, Math.min(vh, Number(item.h) || 560))
      return {
        id: index + 1,
        typeId: item.typeId.slice(0, 64),
        zIndex: DESKTOP_LAYERS.windowBase + 1 + index,
        minimized: !!item.minimized,
        x: Math.max(0, Math.min(vw - w, Number(item.x) || 88)),
        y: Math.max(0, Math.min(vh - h, Number(item.y) || 60)),
        w,
        h,
        snap: typeof item.snap === "string" ? item.snap : null,
        desktopId: typeof item.desktopId === "string" ? item.desktopId.slice(0, 80) : null,
        origin: null,
        payload: null,
      }
    })
  } catch {
    return []
  }
}

function saveWindowSession(windows, accountId, monitorId, enabled) {
  try {
    const key = scopedKey(SESSION_KEY, accountId, monitorId)
    if (!enabled) {
      localStorage.removeItem(key)
      return
    }
    localStorage.setItem(key, JSON.stringify(windows.slice(0, MAX_WINDOWS).map(({ id, typeId, minimized, x, y, w, h, snap, desktopId }) => ({
      id, typeId, minimized, x, y, w, h, snap, desktopId,
    }))))
  } catch { /* optional cache */ }
}

let _nextId = 1

function ruleGeometry(rule, offset) {
  const base = { x: 88 + offset, y: 60 + offset, w: rule?.width ?? 860, h: rule?.height ?? 560, snap: rule?.snap || null }
  if (!rule?.snap) return base
  const left = 64
  const width = Math.max(600, window.innerWidth - left)
  const height = Math.max(360, window.innerHeight)
  if (rule.snap === "full") return { x: left, y: 0, w: width, h: height, snap: "full" }
  if (rule.snap === "l") return { x: left, y: 0, w: width / 2, h: height, snap: "l" }
  if (rule.snap === "r") return { x: left + width / 2, y: 0, w: width / 2, h: height, snap: "r" }
  const right = rule.snap.endsWith("r")
  const bottom = rule.snap.startsWith("b")
  return { x: left + (right ? width / 2 : 0), y: bottom ? height / 2 : 0, w: width / 2, h: height / 2, snap: rule.snap }
}

function rebaseWindowLayers(windows, zTopRef) {
  if (zTopRef.current < DESKTOP_LAYERS.systemPanel - 100) return windows
  const ranks = new Map([...windows].sort((left, right) => left.zIndex - right.zIndex).map((item, index) => [item.id, DESKTOP_LAYERS.windowBase + 1 + index]))
  zTopRef.current = DESKTOP_LAYERS.windowBase + windows.length
  return windows.map((item) => ({ ...item, zIndex: ranks.get(item.id) }))
}

function limitWindowCount(windows, limit) {
  if (windows.length <= limit) return windows
  const removeCount = windows.length - limit
  const evicted = new Set([...windows]
    .sort((left, right) => Number(right.minimized) - Number(left.minimized) || left.zIndex - right.zIndex)
    .slice(0, removeCount)
    .map((item) => item.id))
  return windows.filter((item) => !evicted.has(item.id))
}

export function SpotlightProvider({ children }) {
  const { accountId, monitorStorageId, prefs, productivity, state: desktopState } = useDesktopWorkspace()
  const [open, setOpen]       = useState(false)
  const [windows, setWindows] = useState(() => {
    const restored = loadWindowSession(accountId, monitorStorageId, prefs.restoreWindowSession)
    _nextId = Math.max(1, ...restored.map((item) => item.id + 1))
    return restored.slice(0, prefs.maxOpenWindows)
  })
  const [recent, setRecent]   = useState(() => loadRecent(accountId))
  const zTopRef               = useRef(DESKTOP_LAYERS.windowBase + windows.length)
  const maxWindowsRef         = useRef(prefs.maxOpenWindows)

  useEffect(() => {
    const timer = setTimeout(() => saveWindowSession(windows, accountId, monitorStorageId, prefs.restoreWindowSession), 350)
    return () => clearTimeout(timer)
  }, [accountId, monitorStorageId, prefs.restoreWindowSession, windows])

  useEffect(() => {
    const restored = loadWindowSession(accountId, monitorStorageId, prefs.restoreWindowSession)
    _nextId = Math.max(1, ...restored.map((item) => item.id + 1))
    setWindows(restored.slice(0, maxWindowsRef.current))
    setRecent(loadRecent(accountId))
  }, [accountId, monitorStorageId, prefs.restoreWindowSession])

  useEffect(() => {
    maxWindowsRef.current = prefs.maxOpenWindows
    setWindows((current) => limitWindowCount(current, prefs.maxOpenWindows))
  }, [prefs.maxOpenWindows])

  useEffect(() => {
    const validDesktopIds = new Set(desktopState.desktops.map((desktop) => desktop.id))
    setWindows((current) => current.every((item) => validDesktopIds.has(item.desktopId))
      ? current
      : current.map((item) => validDesktopIds.has(item.desktopId) ? item : { ...item, desktopId: desktopState.activeDesktopId })
    )
  }, [desktopState.activeDesktopId, desktopState.desktops])

  // ── Double-space listener ──────────────────────────────────────────────────
  useEffect(() => {
    let lastSpace = 0
    const onKeyDown = (e) => {
      if (e.code !== "Space") { lastSpace = 0; return }
      const tag = e.target?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) {
        lastSpace = 0
        return
      }
      e.preventDefault()
      const now = Date.now()
      if (now - lastSpace < 380) {
        setOpen(true)
        lastSpace = 0
      } else {
        lastSpace = now
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [])

  const closeSpotlight = useCallback(() => setOpen(false), [])
  const openSpotlight  = useCallback(() => setOpen(true),  [])

  const openWindow = useCallback((typeId, openTerminalFn, payload = null, origin = null) => {
    setOpen(false)
    if (typeId === "terminal") {
      openTerminalFn?.()
      return
    }
    setWindows((prev) => {
      const layered = rebaseWindowLayers(prev, zTopRef)
      const rule = productivity.windowRules.find((item) => item.enabled && item.windowId === typeId)
      const existing = layered.find((w) => w.typeId === typeId)
      if (existing) {
        const newZ = ++zTopRef.current
        const geometry = rule ? ruleGeometry(rule, 0) : {}
        return layered.map((w) => w.typeId === typeId
          ? { ...w, ...geometry, zIndex: newZ, minimized: false, desktopId: desktopState.activeDesktopId, origin, ...(payload === null ? {} : { payload }) }
          : w
        )
      }
      const id = _nextId++
      const offset = (layered.length % 6) * 28
      const geometry = ruleGeometry(rule, offset)
      return limitWindowCount([...layered, {
        id,
        typeId,
        zIndex: ++zTopRef.current,
        minimized: false,
        ...geometry,
        desktopId: desktopState.activeDesktopId,
        origin,
        payload,
      }], prefs.maxOpenWindows)
    })
    setRecent((prev) => {
      const next = [typeId, ...prev.filter((t) => t !== typeId)].slice(0, MAX_RECENT)
      saveRecent(next, accountId)
      return next
    })
  }, [accountId, desktopState.activeDesktopId, prefs.maxOpenWindows, productivity.windowRules])

  const closeWindow = useCallback((id) => {
    setWindows((prev) => prev.filter((w) => w.id !== id))
  }, [])

  const focusWindow = useCallback((id) => {
    setWindows((prev) => {
      const layered = rebaseWindowLayers(prev, zTopRef)
      const newZ = ++zTopRef.current
      return layered.map((w) => w.id === id ? { ...w, zIndex: newZ } : w)
    })
  }, [])

  const minimizeWindow = useCallback((id) => {
    setWindows((prev) => prev.map((w) => w.id === id ? { ...w, minimized: !w.minimized } : w))
  }, [])

  const updateGeom = useCallback((id, geom) => {
    setWindows((prev) => prev.map((w) => w.id === id ? { ...w, ...geom } : w))
  }, [])

  const moveWindowToDesktop = useCallback((id, desktopId) => {
    if (!desktopState.desktops.some((desktop) => desktop.id === desktopId)) return
    setWindows((prev) => prev.map((w) => String(w.id) === String(id) ? { ...w, desktopId } : w))
  }, [desktopState.desktops])

  const pruneWindows = useCallback((validTypeIds) => {
    setWindows((prev) => prev.every((item) => validTypeIds.has(item.typeId))
      ? prev
      : prev.filter((item) => validTypeIds.has(item.typeId))
    )
  }, [])

  const arrangeWindows = useCallback((layout) => {
    setWindows((prev) => {
      const visible = prev.filter((item) => !item.minimized && item.desktopId === desktopState.activeDesktopId)
      if (!visible.length) return prev
      const left = 72
      const top = 10
      const width = Math.max(600, window.innerWidth - left - 20)
      const height = Math.max(360, window.innerHeight - 20)
      const rects = layout === "quarters"
        ? [
          { x: left, y: top, w: width / 2, h: height / 2 },
          { x: left + width / 2, y: top, w: width / 2, h: height / 2 },
          { x: left, y: top + height / 2, w: width / 2, h: height / 2 },
          { x: left + width / 2, y: top + height / 2, w: width / 2, h: height / 2 },
        ]
        : layout === "thirds"
          ? [
            { x: left, y: top, w: width / 3, h: height },
            { x: left + width / 3, y: top, w: width / 3, h: height },
            { x: left + width * 2 / 3, y: top, w: width / 3, h: height },
          ]
          : layout === "main-left" || layout === "main-right"
            ? layout === "main-left"
              ? [
                { x: left, y: top, w: width * 2 / 3, h: height },
                { x: left + width * 2 / 3, y: top, w: width / 3, h: height / 2 },
                { x: left + width * 2 / 3, y: top + height / 2, w: width / 3, h: height / 2 },
              ]
              : [
                { x: left + width / 3, y: top, w: width * 2 / 3, h: height },
                { x: left, y: top, w: width / 3, h: height / 2 },
                { x: left, y: top + height / 2, w: width / 3, h: height / 2 },
              ]
            : layout === "cascade"
          ? visible.map((_, index) => ({ x: left + index * 32, y: top + index * 28, w: Math.max(300, Math.min(860, width - index * 32)), h: Math.max(180, Math.min(560, height - index * 28)) }))
          : [
            { x: left, y: top, w: width / 2, h: height },
            { x: left + width / 2, y: top, w: width / 2, h: height },
          ]
      let index = 0
      return prev.map((item) => {
        if (item.minimized || item.desktopId !== desktopState.activeDesktopId) return item
        const rect = rects[index % rects.length]
        index += 1
        return { ...item, ...rect, snap: null }
      })
    })
  }, [desktopState.activeDesktopId])

  const restoreWindows = useCallback((items) => {
    if (!Array.isArray(items)) return
    const vw = Math.max(640, window.innerWidth)
    const vh = Math.max(420, window.innerHeight)
    const next = items.slice(0, maxWindowsRef.current).filter((item) => item && typeof item.typeId === "string" && item.typeId !== "terminal").map((item, index) => {
      const w = Math.max(300, Math.min(vw, Number(item.w) || 860))
      const h = Math.max(180, Math.min(vh, Number(item.h) || 560))
      return {
        id: _nextId++,
        typeId: item.typeId.slice(0, 64),
        zIndex: ++zTopRef.current,
        minimized: !!item.minimized,
        x: Math.max(0, Math.min(vw - w, Number(item.x) || 88 + index * 24)),
        y: Math.max(0, Math.min(vh - h, Number(item.y) || 60 + index * 24)),
        w,
        h,
        snap: null,
        payload: null,
        desktopId: desktopState.activeDesktopId,
        origin: null,
      }
    })
    setWindows(next)
  }, [desktopState.activeDesktopId])

  const visibleWindows = windows.filter((item) => item.desktopId === desktopState.activeDesktopId)

  return (
    <SpotlightCtx.Provider value={{
      open, openSpotlight, closeSpotlight,
      windows, visibleWindows, openWindow, closeWindow, focusWindow, minimizeWindow, updateGeom, moveWindowToDesktop, arrangeWindows, restoreWindows, pruneWindows,
      recent,
    }}>
      {children}
    </SpotlightCtx.Provider>
  )
}

export function useSpotlight() {
  return useContext(SpotlightCtx)
}
